import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA4_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

// --- Google Analytics 4 -----------------------------------------------------
function loadGA() {
  if (!GA_ID || window.__gaLoaded) return;
  window.__gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // We send page_view manually on each SPA route change.
  window.gtag("config", GA_ID, { send_page_view: false });
}

// --- Microsoft Clarity ------------------------------------------------------
function loadClarity() {
  if (!CLARITY_ID || window.__clarityLoaded) return;
  window.__clarityLoaded = true;
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
}

const SCROLL_MARKS = [25, 50, 75, 100];

export default function Analytics() {
  const location = useLocation();
  const scrollState = useRef({ path: null, reached: new Set() });
  const fbFirstView = useRef(true);

  // Load the SDKs once. GA stays early (light, needed for conversions).
  // The heavy session-recording SDKs (Clarity/PostHog) are deferred until the
  // page has painted + gone idle, to protect LCP on mobile.
  useEffect(() => {
    loadGA();
    const startHeavy = () => setTimeout(loadClarity, 2500);
    if (document.readyState === "complete") {
      startHeavy();
    } else {
      window.addEventListener("load", startHeavy, { once: true });
    }
  }, []);

  // Track a page_view on every route change.
  useEffect(() => {
    // Delay slightly so lazy-loaded pages + PageMeta have set the correct
    // per-language document.title before GA4 records it.
    const t = setTimeout(() => {
      if (window.gtag && GA_ID) {
        window.gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
      if (window.clarity && CLARITY_ID) {
        window.clarity("set", "page", location.pathname);
      }
      // Meta Pixel: fire a PageView on SPA route changes (skip the very first,
      // since the base pixel code in index.html already fired it on load).
      if (typeof window.fbq === "function") {
        if (fbFirstView.current) {
          fbFirstView.current = false;
        } else {
          window.fbq("track", "PageView");
        }
      }
    }, 700);
    // reset scroll tracking for the new page
    scrollState.current = { path: location.pathname, reached: new Set() };
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  // Scroll-depth tracking — focused on the /support page.
  useEffect(() => {
    if (location.pathname !== "/support") return;

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      for (const mark of SCROLL_MARKS) {
        if (pct >= mark && !scrollState.current.reached.has(mark)) {
          scrollState.current.reached.add(mark);
          if (window.gtag && GA_ID) {
            window.gtag("event", "scroll_depth", {
              percent: mark,
              page_path: location.pathname,
            });
          }
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  return null;
}
